import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSchedule } from '@api/schedulePolicies';
import { get, orderBy } from 'lodash';
import {
  scheduleStatus,
  messageMap,
  genSummaryText,
  buildPolicyListTitle,
 policyAction } from '@store/policies/schedule/utils';
import { useSelector } from 'react-redux';
import policyLookupSelector from '@store/policies/schedule/selectors';
import { useState } from 'react';

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

function useFetchSchedules(tableKey) {
  const queryClient = useQueryClient();

  const [sortBy, setSortBy] = useState({
    key: 'triggerAt' as string,
    order: SortOrder.DESC,
  });

  const onColumnSort = ({ key, order }) => {
    setSortBy({ key, order });
  };

  const policyLookup = useSelector(policyLookupSelector);

  const { isFetching, data, isError, refetch } = useQuery(
    ['precision_schedule', 'type', tableKey],
    async () => {
      const res = await getSchedule({ params: { type: tableKey } });

      const { rows }: { rows: any } = get(res, ['data', 'data'], []);

      if (rows.length && tableKey === policyAction.activate) {
        rows.forEach((r) => {
          if (!(r.state in [scheduleStatus.pending, scheduleStatus.discarded]) && r.runLog) {
            r.runSummary = genSummaryText(policyLookup, JSON.parse(r.runLog));
          }
          r.policyNames = buildPolicyListTitle(r.policyList);
        });
      }

      return rows;
    },
    {
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const forceUpdateSchedulesData = () => {
    queryClient.invalidateQueries(['precision_schedule', 'type', tableKey]);
  };

  const orderedRows: any = orderBy(data, [sortBy.key], [sortBy.order]);

  return {
    isFetching,
    orderedRows,
    isError,
    refetch,
    sortBy,
    onColumnSort,
    forceUpdateSchedulesData,
  };
}

export default useFetchSchedules;
